import React, { useState, useEffect, useMemo } from "react";
import {
    getOperationalExpenses,
    deleteOperationalExpense,
    updateOperationalExpense,
    createOperationalExpense,
    getMonthlyStats
} from "../../Api/Api";
import OperationalExpenseModal from "./OperationalExpenseModal";
import Pagination from "../Pagination/Pagination";
import { useWorkshop } from "../Common/WorkshopContext";
import { useNotification } from "../Notification/NotificationContext";
import ConfirmationModal from "../Inventory/DeleteInventoryModal";
import { FaTrash, FaEdit, FaPlus, FaWallet, FaChartLine } from "react-icons/fa";
import MonthlyExpenseChart from "./MonthlyExpenseChart";

const OperationalExpensesList = () => {
    const { workshopId } = useWorkshop();
    const { showNotification } = useNotification();

    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [chartData, setChartData] = useState([]);
    const pageSize = 10;

    const [showModal, setShowModal] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Calculate total for KPI Card
    const totalSpend = useMemo(() =>
            chartData.reduce((acc, curr) => acc + curr.totalAmount, 0),
        [chartData]);

    useEffect(() => {
        if (workshopId) {
            fetchExpenses();
            fetchChartData();
        }
    }, [currentPage, workshopId]);

    const fetchChartData = async () => {
        try {
            const response = await getMonthlyStats(workshopId);
            setChartData(response.data);
        } catch (err) {
            console.error("Error fetching chart stats", err);
        }
    };

    const fetchExpenses = () => {
        setLoading(true);
        getOperationalExpenses(workshopId, currentPage, pageSize)
            .then((response) => {
                setExpenses(response.data.content);
                setTotalPages(response.data.totalPages);
                setTotalCount(response.data.totalElements);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                showNotification(err.message, "error");
            });
    };

    // Helper for Type Badges
    const getTypeStyle = (type) => {
        const styles = {
            TECH: "bg-blue-100 text-blue-700",
            STAFF: "bg-purple-100 text-purple-700",
            RENTAL: "bg-orange-100 text-orange-700",
            MISCELLANEOUS: "bg-gray-100 text-gray-700"
        };
        return styles[type] || "bg-gray-100 text-gray-700";
    };

    const handleSave = async (expenseData) => {
        try {
            await (selectedExpense
                    ? updateOperationalExpense(workshopId, selectedExpense.id, expenseData)
                    : createOperationalExpense(workshopId, expenseData)
            );
            showNotification(`Expense ${selectedExpense ? "updated" : "added"} successfully`, "success");
            setShowModal(false);
            setSelectedExpense(null);
            fetchExpenses();
            fetchChartData(); // Refresh chart after adding/editing
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    const handleDelete = async () => {
        try {
            await deleteOperationalExpense(selectedExpense.id);
            showNotification("Expense deleted successfully", "success");
            setShowDeleteConfirm(false);
            setSelectedExpense(null);
            fetchExpenses();
            fetchChartData();
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    return (
        <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
            <div className="max-w-screen-xl mx-auto space-y-8">

                {/* 1. Header & Actions */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Operational Expenses</h1>
                        <p className="text-gray-500">Manage and track your workshop's daily overheads.</p>
                    </div>
                    <button
                        onClick={() => { setSelectedExpense(null); setShowModal(true); }}
                        className="inline-flex items-center px-5 py-2.5 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition shadow-sm"
                    >
                        <FaPlus className="mr-2" /> Add New Expense
                    </button>
                </div>

                {/* 2. KPI Cards & Chart Section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        <MonthlyExpenseChart data={chartData} />
                    </div>
                    <div className="space-y-6">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-teal-50 text-teal-600 rounded-lg"><FaWallet size={24}/></div>
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Total Period Spending</p>
                                    <h3 className="text-2xl font-bold text-gray-900">₹{totalSpend.toLocaleString('en-IN')}</h3>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-blue-50 text-blue-600 rounded-lg"><FaChartLine size={24}/></div>
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Total Transactions</p>
                                    <h3 className="text-2xl font-bold text-gray-900">{totalCount}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. Data Table Section */}
                <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                        <h3 className="font-bold text-gray-800 text-lg">Detailed Transactions</h3>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-semibold">
                            <tr>
                                <th className="px-6 py-4">ID</th>
                                <th className="px-6 py-4">Category</th>
                                <th className="px-6 py-4 text-right">Amount</th>
                                <th className="px-6 py-4">Date</th>
                                <th className="px-6 py-4 text-center">Actions</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                [...Array(3)].map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="5" className="px-6 py-4 bg-gray-50 h-12"></td>
                                    </tr>
                                ))
                            ) : expenses.length > 0 ? (
                                expenses.map((exp) => (
                                    <tr key={exp.id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-400">#{exp.id}</td>
                                        <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTypeStyle(exp.expenseType)}`}>
                                                    {exp.expenseType}
                                                </span>
                                        </td>
                                        <td className="px-6 py-4 text-right font-bold text-gray-900">₹{exp.amount.toLocaleString('en-IN')}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {new Date(exp.expenseDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center gap-3">
                                                <button
                                                    onClick={() => { setSelectedExpense(exp); setShowModal(true); }}
                                                    className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition"
                                                    title="Edit"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    onClick={() => { setSelectedExpense(exp); setShowDeleteConfirm(true); }}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                                    title="Delete"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center py-12 text-gray-400">
                                        No expenses recorded for this period.
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>

                    <div className="p-4 border-t border-gray-100">
                        <Pagination
                            totalPages={totalPages}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                            totalCount={totalCount}
                        />
                    </div>
                </div>
            </div>

            {/* Modals remain the same */}
            <OperationalExpenseModal
                show={showModal}
                expense={selectedExpense}
                onClose={() => { setShowModal(false); setSelectedExpense(null); }}
                onSave={handleSave}
            />

            <ConfirmationModal
                show={showDeleteConfirm}
                message="Delete this expense record? This cannot be undone."
                onConfirm={handleDelete}
                onCancel={() => { setShowDeleteConfirm(false); setSelectedExpense(null); }}
            />
        </div>
    );
};

export default OperationalExpensesList;